import AuthService from "../api/auth/ApiAuthService";
import { Mutex } from 'async-mutex';
import CookieService from './CookieService';
import { jwtDecode } from "jwt-decode";
import xsifterAppStore from "../store/XSifterAppStore";
import { IXSifterAppUser, IXSifterDiscoverTextSitesResponse, IXSifterUser } from "../types/XSifterAppUser";
import { setCurrentUser, setDisplayMessage, setDisplayMessageType } from "../store/XSifterAppReducer";
import { IResponseBase } from "../types/XSifterGeneralTypes";

export default class UserAuthService {
    public static readonly TOKEN_RENEWAL_CHECK_INTERVAL_MS = 5000;
    public static readonly TOKEN_RENEWAL_THRESHOLD_MS = 30000;

    private RENEWAL_BUFFER_SECONDS = 120;
    private DEFAULT_TOKEN_RENEWAL = 600;

    private _token: string | null = null;
    private _tokenRenewalSeconds: number = this.DEFAULT_TOKEN_RENEWAL;
    private _tokenRenewTimestamp: number = -1;
    private _tokenMutex = new Mutex();
    private _authService = new AuthService();

    private static instance: UserAuthService;

    private _dispatch = xsifterAppStore.dispatch;
    private _currentUser: IXSifterUser | undefined;

    private constructor() {
        this.checkTokenCookie();
    }

    public static getInstance(): UserAuthService {
        if (!UserAuthService.instance) {
            UserAuthService.instance = new UserAuthService();
        }
        return UserAuthService.instance;
    }

    public setCurrentUser(user: IXSifterUser | undefined) {
        this._currentUser = user;
        this._dispatch(setCurrentUser(user));
    }

    public getApiBaseUrlForHostname(hostname: string): string {
        let hosts = process.env.DT_API_HOSTS.split(";");
        for (let i=0; i < hosts.length; i++) {
            let split = hosts[i].split('=');
            if (split.length === 2 && split[0] === hostname)
                return split[1];
        }
        return "";
    }

    public getBaseApiUrlForToken(token: string) : string {
        const decoded = jwtDecode(token);
        if (!decoded)
            return "";
        let hostname = decoded['hostname'];
        return this.getApiBaseUrlForHostname(hostname ?? "");
    }

    public getTokenAppUser(): IXSifterAppUser | undefined {
        if (!this._token) {
            return undefined;
        }

        const decoded = jwtDecode(this._token);
        if (!decoded) {
            return undefined;
        }

        let hostname = decoded['hostname'];
        let apiBaseUrl = this.getApiBaseUrlForHostname(hostname);

        return {
            userId: decoded['userid'],
            username: decoded['username'],
            hostname: hostname,
            apiBaseUrl: apiBaseUrl
        }
    }

    public async getDiscoverTextSites(): Promise<IXSifterDiscoverTextSitesResponse> {
        return this._authService.getDiscoverTextSites();
    }

    public getOAuthUrl(hostname: string): Promise<string> {
        const currentHost = `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;
        const redirectUri=`${currentHost}/oauth/login`;
        return new Promise<string>((resolve, reject) => {
            this._authService.getOAuthLoginUrl(redirectUri, hostname)
                .then((response) => {
                    resolve(response.oAuthUrl);
                })
                .catch((error: IResponseBase) => {
                    reject(error);
                })
        });
    }

    public async exchangeCodeForToken(code: string, hostname: string) : Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this._authService.exchangeCodeForToken(code, hostname)
            .then((tokenResponse) => {
                return this._authService.loginToXSifter(tokenResponse.token || null)
                    .then((response) => {
                        let xSifterAppUser = response as IXSifterUser;
                        this.setToken(tokenResponse.token || "", tokenResponse.expiresIn || this.DEFAULT_TOKEN_RENEWAL);
                        this._dispatch(setCurrentUser(xSifterAppUser));
                        this._dispatch(setDisplayMessage("Login successful"));
                        this._dispatch(setDisplayMessageType("success"));
                        resolve();
                    })
            })
            .catch((error: IResponseBase) => {
                reject(error);
            })
        });
    }

    getToken(): string | null {
        if (!this._token)
            return null;

        this.renewToken();
        return this._token;
    }

    public async refreshCurrentUser() : Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this._authService.getCurrentUser(this.getToken() ?? "")
                .then((user) => {
                    if (user) {
                        this.setCurrentUser(user);
                        this._dispatch(setCurrentUser(user));
                        resolve();
                    } else {
                        this.setCurrentUser(undefined);
                        this._dispatch(setCurrentUser(undefined));
                        reject(false);
                    }
                }).catch((error: IResponseBase) => {
                    this.setCurrentUser(undefined);
                    this._dispatch(setCurrentUser(undefined));
                    reject(false);
                })
        });
    }

    async getCurrentUser(): Promise<IXSifterUser | undefined> {
        return this._authService.getCurrentUser(this.getToken() ?? "");
    }

    getSecondsUntilRenewal(): number {
        if (!this._token)
            return -1;
        return (this._tokenRenewTimestamp - (new Date()).getTime());
    }

    getXSifterAppUser(): IXSifterUser | undefined {
        return this._currentUser;
    }

    isLoggedIn(): boolean {
        return (this._token !== undefined && this._token !== null && this._currentUser !== undefined && this._currentUser !== null);
    }

    async isApproved(): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this._authService.getCurrentUser(this.getToken() ?? "")
                .then((response) => {
                    resolve(response?.role === "admin" || response?.role === "user");
                }).catch(() => {
                    resolve(false);
                });
        })
    }

    isAdmin(): boolean {
        if (!this._currentUser)
            return false;
        return (this._currentUser.role === "admin");
    }

    async acknowledgeRules(): Promise<boolean> {
        if (!this._currentUser)
            return new Promise<boolean>((resolve, reject) => { reject(); })
        
        return new Promise<boolean>((resolve, reject) => {
            this._authService.acknowledgeRules(this.getToken() ?? "")
            .then((response) => {
                if (!response || !this._currentUser)
                    reject();
                else {
                    this._dispatch(setCurrentUser({
                        ...this._currentUser,
                        hasAck: true,
                    }));
                    resolve(true);
                }
            })
            .catch((error: IResponseBase) => {
                reject(error);
            })
        })
    }
    
    async renewToken() {
        if (!this._token)
            return;

        if (!this.isTokenExpired())
            return;

        await this._tokenMutex.runExclusive(async () => {
            await this.refreshToken();
        });
    };

    private async refreshToken() {
        if (!this.isLoggedIn()) {
            if (this.tokenCookieExists()) {
                this.clearToken();
            }
            return;
        }

        let localThis = this;
        let apiBaseUrl = this.getBaseApiUrlForToken(this._token ?? "");

        return this._authService.refresh(apiBaseUrl, this._token || "")
            .then((response) => {
                if (response.error) {
                    localThis.clearToken();
                    return;
                }

                localThis.setToken(response.token || "", response.expiresIn || -1)
           }).catch((error: IResponseBase) => {
                if (error?.error?.statusCode === 401 || error?.error?.statusCode === 403) {
                    this.logout();
                }
           });
    }

    logout() {
        this.clearToken();
        this._dispatch(setDisplayMessage(undefined));
        this._dispatch(setDisplayMessageType(undefined));
    }

    private setToken(token: string, renewInSeconds: number) {
        if (!token || token === "") {
            return;
        }

        this._token = token;
        if (renewInSeconds > 0) {
            this._tokenRenewalSeconds = renewInSeconds;
        } else {
            this._tokenRenewalSeconds = this.DEFAULT_TOKEN_RENEWAL;
        }
        let msAdd = (this._tokenRenewalSeconds * 1000) - (this.RENEWAL_BUFFER_SECONDS * 1000);
        if (msAdd < 0)
            msAdd = 30000;
        this._tokenRenewTimestamp = (new Date()).getTime() + msAdd;
        this.setTokenCookie(renewInSeconds);
    }

    private isTokenExpired() {
        return (this._tokenRenewTimestamp < (new Date().getTime()));
    }

    private clearToken() {
        this._token = null;
        this._tokenRenewTimestamp = -1;
        this._dispatch(setCurrentUser(undefined));
        this.setTokenCookie();
    }

    private tokenCookieExists(): boolean {
        let cookies = new CookieService();
        let authCookie = cookies.getCookie(process.env.AUTH_COOKIE_KEY);
        if (!authCookie) {
            this._dispatch(setCurrentUser(undefined));
            return false;
        }
        return true;
    }

    private checkTokenCookie() {
        let cookies = new CookieService();
        let authCookie = cookies.getCookie(process.env.AUTH_COOKIE_KEY);
        if (!authCookie) {
            this._dispatch(setCurrentUser(undefined));
            return;
        }

        let parts = authCookie.split('##', 3);
        if (parts.length !== 3) {
            this._dispatch(setCurrentUser(undefined));
            return;
        }

        this._tokenRenewTimestamp = parseInt(parts[0]);
        this._token = parts[1];
        let userPart = parts[2];
        if (!userPart || userPart === undefined || userPart === "undefined") {
            this.refreshCurrentUser();
            return;
        }
        this._currentUser = JSON.parse(userPart) as IXSifterUser;
        this._dispatch(setCurrentUser(this._currentUser));
    }

    private setTokenCookie(renewSeconds?: number) {
        let cookies = new CookieService();
        if (!this._token) {
            cookies.deleteCookie(process.env.AUTH_COOKIE_KEY);
            return;
        }

        let authValue = this._currentUser ? `${this._tokenRenewTimestamp}##${this._token}##${JSON.stringify(this._currentUser)}` : `${this._tokenRenewTimestamp}##${this._token}##`;
        if (renewSeconds) {
            cookies.setCookie(process.env.AUTH_COOKIE_KEY, authValue, new Date((new Date()).getTime() + (renewSeconds*1000)));
        } else {
            cookies.setCookie(process.env.AUTH_COOKIE_KEY, authValue);
        }
    }
};

